import * as styles from "./ContractEditorDialog.css";

import { useAtomValue } from "jotai";
import type { JSX, RefObject } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { Trans, useLingui } from "@lingui/react/macro";

import { ContractEditorBody } from "./components/ContractEditorBody";
import {
  type UseContractEditorOptions,
  useContractEditor,
} from "./hooks/useContractEditor";
import { contractEditorAtom, contractForm } from "./stores/editor";

const DIALOG_WIDTH = 960;

export type ContractEditorDialogProps = UseContractEditorOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the dialog grows from: the row or the button that opened it. */
  originRef?: RefObject<DOMRect | null>;
};

export function ContractEditorDialog({
  open,
  onOpenChange,
  projectId,
  contractId = null,
  onSaved,
  onCancel,
  originRef,
}: ContractEditorDialogProps): JSX.Element {
  const { t } = useLingui();

  const version = useAtomValue(contractEditorAtom).version;
  const dirty = useAtomValue(contractForm.state.isDirty);

  const { loading, saving, error, conflict, save, cancel } = useContractEditor({
    projectId,
    contractId,
    onSaved: (savedId) => {
      onOpenChange(false);
      onSaved?.(savedId);
    },
    onCancel: () => {
      onOpenChange(false);
      onCancel?.();
    },
  });

  const guard = useDiscardGuard({ dirty, onClose: cancel });

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH} className={styles.popup}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            {null === contractId ? t`New contract` : t`Contract`}

            {null !== version ? (
              <span className={styles.subtitle}>{t`Version ${version}`}</span>
            ) : null}
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport fade="end">
            <ScrollAreaContent
              className={styles.scroller}
              style={{ minWidth: 0 }}
            >
              <ContractEditorBody
                loading={loading}
                error={error}
                conflict={conflict}
                stickyTabs
              />
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <DialogFooter>
          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={loading}
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
